import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Compass } from 'lucide-react';

interface AuthShellProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}

/** Centered card layout shared by every auth page (login, signup, reset…). */
export function AuthShell({ title, subtitle, children }: AuthShellProps) {
  return (
    <div className="flex min-h-screen flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 shadow-lg shadow-blue-200">
            <Compass className="h-6 w-6 text-white" />
          </span>
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Teskedo</span>
        </Link>
        <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100">{title}</h2>
        {subtitle && <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">{subtitle}</p>}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-8 shadow-xl shadow-gray-200/50 sm:rounded-3xl sm:px-10">
          {children}
        </div>
      </div>
    </div>
  );
}

/** Small inline alert used across auth forms for errors / success notices. */
export function AuthAlert({ tone, children }: { tone: 'error' | 'success'; children: ReactNode }) {
  const styles =
    tone === 'error'
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : 'border-emerald-200 bg-emerald-50 text-emerald-700';
  return <div className={`rounded-xl border px-4 py-3 text-sm ${styles}`}>{children}</div>;
}
